import { useEffect, useMemo, useState } from "react";
import { TriggerSettingMouseVisualizer } from "../TriggerSettingMouseVisualizer";
import TriggerSettingPanel, {
  deviceFastTriggerPowerToUi,
  deviceTactileFeedbackToUi,
  deviceTriggerToUiForProfile,
  getTriggerPointProfile,
  type TriggerButtonTarget,
} from "../../../../../mouse-management/src/pages/triggerSetting/TriggerSetting";
import {
  mouseIsAlpha6,
  mouseSupportsTriggerMagneticExtras,
} from "../../../../../mouse-management/src/domain/mouseIdentity";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import { getMouseTriggerValues } from "../utils/mouseConfigPreviewUtils";
import "../../../../../mouse-management/src/pages/triggerSetting/index.scss";
import "../../../../../mouse-management/src/pages/triggerSetting/Mouse.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

const noop = () => undefined;

export default function MouseTriggerSection({ meta, configData }: Props) {
  const deviceId = meta.mouseDeviceId;
  const triggerPointProfile = getTriggerPointProfile(deviceId);
  const showMagneticExtras = mouseSupportsTriggerMagneticExtras(deviceId);

  const trigger = useMemo(
    () => getMouseTriggerValues(configData, deviceId),
    [configData, deviceId]
  );

  const [selectedButton, setSelectedButton] = useState<TriggerButtonTarget>(
    trigger.selectedButton
  );

  useEffect(() => {
    setSelectedButton(trigger.selectedButton);
  }, [trigger.selectedButton]);

  const leftPoint = useMemo(
    () =>
      deviceTriggerToUiForProfile(
        trigger.left.triggerPoint,
        triggerPointProfile
      ),
    [trigger.left.triggerPoint, triggerPointProfile]
  );
  const rightPoint = useMemo(
    () =>
      deviceTriggerToUiForProfile(
        trigger.right.triggerPoint,
        triggerPointProfile
      ),
    [trigger.right.triggerPoint, triggerPointProfile]
  );

  const displaySide = selectedButton === "right" ? trigger.right : trigger.left;
  const displayTriggerPoint =
    selectedButton === "right" ? rightPoint : leftPoint;

  const fastTriggerPowerUi = deviceFastTriggerPowerToUi(
    displaySide.fastTrigger,
    displaySide.fastTriggerState === 1
  );
  const tactileFeedbackUi = deviceTactileFeedbackToUi(
    displaySide.tactileFeedback
  );

  return (
    <div className="community-mouse-trigger-preview trigger-page mouse-trigger-page">
      <div className="trigger-aside">
        <TriggerSettingMouseVisualizer
          deviceId={deviceId}
          displayname={meta.displayname}
          left={{
            triggerPoint: leftPoint,
            fastTriggerEnabled: trigger.left.fastTriggerState === 1,
            fastTriggerPowerUi: deviceFastTriggerPowerToUi(
              trigger.left.fastTrigger,
              trigger.left.fastTriggerState === 1
            ),
            tactileFeedbackUi: deviceTactileFeedbackToUi(
              trigger.left.tactileFeedback
            ),
          }}
          right={{
            triggerPoint: rightPoint,
            fastTriggerEnabled: trigger.right.fastTriggerState === 1,
            fastTriggerPowerUi: deviceFastTriggerPowerToUi(
              trigger.right.fastTrigger,
              trigger.right.fastTriggerState === 1
            ),
            tactileFeedbackUi: deviceTactileFeedbackToUi(
              trigger.right.tactileFeedback
            ),
          }}
          showMagneticExtras={showMagneticExtras}
        />
      </div>
      <div className="trigger-main community-preview-interactive">
        <TriggerSettingPanel
          disabled
          selectedButton={selectedButton}
          onSelectedButtonChange={setSelectedButton}
          triggerPoint={displayTriggerPoint}
          currentRoute={0}
          onTriggerPointChange={noop}
          onTriggerPointComplete={noop}
          showMagneticExtras={showMagneticExtras}
          showButtonSound={false}
          triggerPointProfile={triggerPointProfile}
          tipsVariant={mouseIsAlpha6(deviceId) ? "alpha6" : "default"}
          fastTriggerEnabled={displaySide.fastTriggerState === 1}
          fastTriggerPower={fastTriggerPowerUi}
          onFastTriggerEnabledChange={noop}
          onFastTriggerPowerChange={noop}
          onFastTriggerPowerComplete={noop}
          vibrationEnabled={tactileFeedbackUi > 0}
          vibrationPower={tactileFeedbackUi}
          onVibrationEnabledChange={noop}
          onVibrationPowerChange={noop}
          onVibrationPowerComplete={noop}
          buttonSoundEnabled={displaySide.buttonSound === 1}
          onButtonSoundEnabledChange={noop}
        />
      </div>
    </div>
  );
}
